<script setup lang="ts">
  import { ChartPieIcon, ChevronUpIcon } from '@heroicons/vue/solid';
  import AnnotationIcon from '~/assets/Icons/AnnotationIcon.vue';
  import ComponentSection from '~/components/UI/Drawers/ComponentSection.vue';
  import { formatToIndianCurrency } from '~/functions/currencyUtilities';
  import type { BudgetsDrawerType, LineItem } from '~/types';
  import TopToRightAngleIcon from '~/assets/Icons/TopToRightAngleIcon.vue';
  import BudgetExpensesStatus from '../../Others/BudgetExpensesStatus.vue';
  import AnnotationOutlinedIcon from '~/assets/Icons/AnnotationOutlinedIcon.vue';

  const props = defineProps<{
    items: BudgetsDrawerType['selected_budget']['line_items'];
    budgetId: string;
    budgetFinancialYear: string;
    budgetName: string;
  }>();

  const isModalVisible = ref(false);
  const currentLineItemKey = ref<string | null>(null);
  const currentSubItemKey = ref<string | null>(null);
  const modalNote = ref<string>('');

  type StructuredItems = {
    main_line_item: LineItem;
    sub_line_items: LineItem[];
  };

  const handleModalCancel = () => {
    isModalVisible.value = false;
    modalNote.value = '';
  };

  const handleModalOk = () => {
    isModalVisible.value = false;

    // find main line item
    const lineItem = groupedArray.value.find(g => g.main_line_item.li_text === currentLineItemKey.value);

    if (!lineItem) return;

    if (currentSubItemKey.value) {
      const subItem = lineItem.sub_line_items.find(s => s.li_subtext === currentSubItemKey.value);
      if (subItem) subItem.li_note = modalNote.value;
    } else {
      lineItem.main_line_item.li_note = modalNote.value;
    }

    modalNote.value = '';
  };

  const structuredItems = computed(() => {
    return props.items.reduce(
      (acc, item) => {
        if (!acc[item.li_text]) {
          acc[item.li_text] = {
            main_line_item: {
              li_text: item.li_text,
              li_amount: 0,
              li_note: item.li_note,
              approved_amount: 0,
              unapproved_amount: 0,
              stats: {
                approved_expenses: {
                  exceeding_budget: 0,
                  within_budget: 0
                },
                unapproved_expenses: {
                  exceeding_budget: 0,
                  within_budget: 0
                },
                total_budget_amount: 0
              }
            },
            sub_line_items: []
          };
        }

        // Only add to sub_line_items if it's not a single item with empty li_subtext
        if (!(acc[item.li_text].sub_line_items.length === 0 && item.li_subtext === '')) {
          acc[item.li_text].sub_line_items.push(item);
        }

        const main = acc[item.li_text].main_line_item;
        main.li_amount += Number(item.li_amount);
        main.approved_amount += item.approved_amount;
        main.unapproved_amount += item.unapproved_amount;
        main.stats.approved_expenses.exceeding_budget += item.stats.approved_expenses.exceeding_budget;
        main.stats.approved_expenses.within_budget += item.stats.approved_expenses.within_budget;
        main.stats.unapproved_expenses.exceeding_budget += item.stats.unapproved_expenses.exceeding_budget;
        main.stats.unapproved_expenses.within_budget += item.stats.unapproved_expenses.within_budget;
        main.stats.total_budget_amount = String(
          Number(main.stats.total_budget_amount) + Number(item.stats.total_budget_amount)
        );

        return acc;
      },
      {} as Record<string, any>
    );
  });

  const groupedArray: Ref<StructuredItems[]> = computed(() => Object.values(structuredItems.value));

  const expandablePanelIndices = computed(() =>
    groupedArray.value
      .map((group, index) => (group.sub_line_items.length > 0 ? index : -1))
      .filter(index => index !== -1)
  );

  const activeKey: Ref<number[]> = ref(computed(() => expandablePanelIndices.value).value);
  const isExpanded = ref(true);

  const handleToggleAll = () => {
    if (isExpanded.value) {
      activeKey.value = [];
    } else {
      activeKey.value = expandablePanelIndices.value;
    }
    isExpanded.value = !isExpanded.value;
  };

  onMounted(() => {
    activeKey.value = expandablePanelIndices.value;
  });
</script>

<template>
  <ComponentSection title="Budget Line Item">
    <template #callToAction>
      <a-button
        class="bg-transparent border-none shadow-none sm-medium text-zinc-600 p-0 h-fit w-fit"
        @click="handleToggleAll"
      >
        {{ isExpanded ? 'Collapse All' : 'Expand All' }}
      </a-button>
    </template>
    <a-collapse
      v-for="(group, index) in groupedArray"
      :key="index"
      v-model:active-key="activeKey"
      collapsible="header"
      class="budget-line-item"
    >
      <template #expandIcon="{ isActive }">
        <ChevronUpIcon class="text-black w-4 h-4" :class="{ 'rotate-180': !isActive }" />
      </template>
      <a-collapse-panel
        :key="index"
        :collapsible="group.sub_line_items.length <= 0 ? 'disabled' : 'header'"
        :show-arrow="group.sub_line_items.length <= 0 ? false : true"
      >
        <template #header>
          <div class="w-full h-full flex items-center justify-between px-3 py-2">
            <div class="flex w-4/5 justify-between">
              <div class="flex items-center max-w-[350px] overflow-ellipsis">
                <p class="sm-medium text-zinc-800">{{ group?.main_line_item?.li_text }}</p>
              </div>
              <div class="flex items-center gap-3">
                <p class="sm-medium text-zinc-800 whitespace-nowrap">
                  {{ formatToIndianCurrency(+group?.main_line_item?.li_amount) }}
                </p>
                <div
                  v-if="group.main_line_item.li_note"
                  class="relative cursor-pointer"
                  @click="
                    () => {
                      isModalVisible = true;
                      currentLineItemKey = group.main_line_item.li_text;
                      currentSubItemKey = null;
                      modalNote = group.main_line_item.li_note || '';
                    }
                  "
                >
                  <div class="absolute -top-0 -right-0 w-3 h-3 bg-blue-700 rounded-full"></div>
                  <AnnotationOutlinedIcon class="w-6 h-6 text-zinc-400 cursor-not-allowed" />
                </div>
                <ChartPieIcon class="w-8 h-8 text-zinc-400" />
                <BudgetExpensesStatus class="min-w-[300px]" :stats="group?.main_line_item?.stats" />
              </div>
            </div>
            <NuxtLink
              target="_blank"
              :to="{
                path: '/expenses',
                query: {
                  budget: `${budgetId}-${budgetName}-${budgetFinancialYear}`,
                  budget_li: group.main_line_item.li_text,
                  lineItem: 'yes'
                }
              }"
              class="flex text-blue-700 hover:underline"
            >
              View
            </NuxtLink>
          </div>
        </template>
        <div
          v-for="item in group.sub_line_items"
          :key="item.li_text"
          class="flex flex-col overflow-hidden rounded-b-lg"
        >
          <div class="w-full h-full flex items-center justify-between px-3 py-2 bg-zinc-50 border-t border-zinc-200">
            <div class="flex w-4/5 justify-between items-center">
              <div class="flex gap-2 max-w-[350px] items-center overflow-ellipsis">
                <TopToRightAngleIcon class="mb-2" />
                <p class="sm-medium text-zinc-800">{{ item?.li_subtext }}</p>
              </div>
              <div class="flex items-center gap-3">
                <p class="sm-medium text-zinc-800 whitespace-nowrap">{{ formatToIndianCurrency(+item?.li_amount) }}</p>
                <div
                  v-if="item.li_note !== undefined"
                  class="relative cursor-pointer"
                  @click="
                    () => {
                      isModalVisible = true;
                      currentLineItemKey = group.main_line_item.li_text;
                      currentSubItemKey = item.li_subtext;
                      modalNote = item.li_note || '';
                    }
                  "
                >
                  <div v-if="item.li_note" class="absolute -top-0 -right-0 w-3 h-3 bg-blue-700 rounded-full"></div>
                  <AnnotationIcon class="w-6 h-6 text-zinc-400" />
                </div>
                <ChartPieIcon class="w-8 h-8 text-zinc-400" />
                <BudgetExpensesStatus class="min-w-[300px]" :stats="item?.stats" />
              </div>
            </div>
            <NuxtLink
              target="_blank"
              :to="{
                path: '/expenses',
                query: {
                  budget: `${budgetId}-${budgetName}-${budgetFinancialYear}`,
                  budget_li: group.main_line_item.li_text + ' - ' + item?.li_subtext,
                  lineItem: 'yes'
                }
              }"
              class="flex text-blue-700 hover:underline"
            >
              <!-- <EyeIconOutlined /> -->
              View
            </NuxtLink>
          </div>
        </div>
      </a-collapse-panel>
    </a-collapse>
    <a-modal :open="isModalVisible" class="account-info-modal" title="Notes" @cancel="handleModalCancel">
      <a-form layout="vertical">
        <a-form-item>
          <a-textarea v-model:value="modalNote" :rows="4" placeholder="Type Something" />
        </a-form-item>
      </a-form>
      <template #footer>
        <div class="flex gap-1 w-full justify-end border-t border-gray-300 p-3 bg-gray-50 rounded-b-lg">
          <a-button
            class="rounded text-sm font-medium border border-gray-300 text-gray-500 px-3 py-1.5"
            :disabled="true"
            @click="handleModalCancel"
            >Cancel</a-button
          >
          <a-button
            class="px-3 border border-blue-700 rounded py-1.5 flex items-center justify-center text-sm font-medium disabled:!border-gray-300"
            type="primary"
            :disabled="true"
            @click="handleModalOk"
            >Save Note</a-button
          >
        </div>
      </template>
    </a-modal>
  </ComponentSection>
</template>

<style>
  .budget-line-item .ant-collapse-header {
    padding: 0px !important;
    display: flex;
    position: relative;
  }

  .budget-line-item .ant-collapse-header-text {
    width: 100%;
  }

  .budget-line-item .ant-collapse-expand-icon {
    position: absolute;
    left: 10px;
    border: 1px solid #d4d4d8;
    padding: 0px !important;
    top: 65px;
    background: #fff;
    height: 20px !important;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    z-index: 1;
  }

  .budget-line-item .ant-collapse-expand-icon svg {
    transition: transform 0.2s ease;
  }

  .budget-line-item .ant-collapse-content-box {
    padding: 0px !important;
  }
</style>
